import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addUserThunk,
  bulkDeleteUsersThunk,
  bulkStatusChangeThunk,
  deleteUserThunk,
  fetchUsersThunk,
  selectUsers,
  selectUsersError,
  selectUsersLoading,
  selectUsersProcessing,
  selectUsersTotal,
  updateUserThunk,
} from "../store/usersSlice";

const useUsers = () => {
  const dispatch = useDispatch();
  const users = useSelector(selectUsers);
  const isLoading = useSelector(selectUsersLoading);
  const isProcessing = useSelector(selectUsersProcessing);
  const error = useSelector(selectUsersError);
  const total = useSelector(selectUsersTotal);

  // on first render, fetch users list (this is the moment when API loading starts.)
  const fetchUsers = useCallback(async () => {
    await dispatch(fetchUsersThunk());
  }, [dispatch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAddUser = useCallback(async (formData) => {
    const action = await dispatch(addUserThunk(formData));
    if (addUserThunk.fulfilled.match(action)) {
      return { success: true, userId: action.payload?.userId ?? null };
    }

    return {
      error: action.payload || "Unable to add user.",
    };
  }, [dispatch]);

  const handleUpdateUser = useCallback(async (id, formData, existingUser) => {
    const action = await dispatch(updateUserThunk({ id, formData, existingUser }));
    if (updateUserThunk.fulfilled.match(action)) {
      return { success: true };
    }

    return {
      error: action.payload || "Unable to update user.",
    };
  }, [dispatch]);

  const handleDeleteUser = useCallback(async (id) => {
    const action = await dispatch(deleteUserThunk(id));
    if (deleteUserThunk.fulfilled.match(action)) {
      return { success: true };
    }

    return {
      error: action.payload || "Unable to delete user.",
    };
  }, [dispatch]);

  const handleBulkDelete = useCallback(async (ids) => {
    const action = await dispatch(bulkDeleteUsersThunk(ids));
    if (bulkDeleteUsersThunk.fulfilled.match(action)) {
      return { success: true };
    }

    return {
      error: action.payload || "Unable to delete selected users.",
    };
  }, [dispatch]);

  const handleBulkStatusChange = useCallback(async (ids, status) => {
    const action = await dispatch(bulkStatusChangeThunk({ ids, status }));
    if (bulkStatusChangeThunk.fulfilled.match(action)) {
      return { success: true };
    }

    return {
      error: action.payload || "Unable to update selected users.",
    };
  }, [dispatch]);

  return {
    users,
    isLoading,
    isProcessing,
    error,
    total,
    fetchUsers,
    handleAddUser,
    handleUpdateUser,
    handleDeleteUser,
    handleBulkDelete,
    handleBulkStatusChange,
  };
};

export default useUsers;
